import mixin from '../mixin'

/**
 * Core animation class; enables delta timing,
 * tick callbacks and running tweens.
 */
export default class Animator {
	/**
	 * Vue.js entrypoint.
	 *
	 * @memberof Animator
	 */
	install(Vue, { fps } = Animator.DEFAULTS) {
		this._fps = 1
		this._frame = null
		this._frameMs = 1000 / fps
		this._lastTick = Date.now()
		this._delta = 0
		this._tweenId = 0

		// Update callbacks registered by components
		this._callbacks = new Set()

		// Add Vue props and mixin
		Vue.prototype.$animator = this
		Vue.prototype.$fps = fps
		Vue.mixin(mixin)

		// Start loop
		this.tick = this._tick.bind(this)
		this.tick()
	}

	/**
	 * Main animation tick. Uses requestAnimationFrame to run
	 * registered callbacks at a consistent fps.
	 *
	 * @memberof Animator
	 */
	_tick() {
		// Basic delta timing
		// Add time elapsed since last animFrame to delta
		// If delta > frameMs, run one tick of game logic
		const now = Date.now()
		this._delta += now - this._lastTick
		if (this._delta >= this._frameMs) {
			// Remove frame deficit from delta time
			this._delta %= this._frameMs

			// Run any update callbacks
			if (this._callbacks.size > 0) {
				for (const cb of this._callbacks) cb()
			}
		}

		// Save timestamp of this tick
		// and request next animation frame
		this._lastTick = now
		this._frame = window.requestAnimationFrame(this.tick)
	}

	/**
	 * Registers a callback for the animator to run.
	 *
	 * @param {function} callback The callback function
	 * @memberof Animator
	 */
	register(callback) {
		this._callbacks.add(callback)
	}

	/**
	 * Deregisters a callback, removing it from animation loop.
	 *
	 * @param {function} callback The callback to deregister
	 * @memberof Animator
	 */
	deregister(callback) {
		if (this._callbacks.has(callback)) this._callbacks.delete(callback)
	}

	/**
	 * Default Animator fps.
	 *
	 * @readonly
	 * @static
	 * @memberof Animator
	 */
	static get DEFAULTS() {
		return {
			fps: 60
		}
	}
}
